import { useState } from "react";
import {
  Avatar,
  Center,
  Flex,
  Table,
  Text,
  useMantineTheme,
  Button,
  Skeleton,
  Tabs,
  Collapse,
} from "@mantine/core";
import { Breadcrumb, InfoPredix, PredictiveBuyCard } from "../../components";
import { useParams, useNavigate } from "react-router-dom";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";
import { Profile } from "iconsax-react";
import PredictiveDateSelector from "./PredictiveDateSelector";
import {
  usePredictiveProduct,
  EInstrumentType,
  ETypeInstrument,
  IPredictiveProductInstrument,
} from "../../hooks/usePredictives";
import { whitelabel_id } from "../../api";
import { meetingDateFormatted } from "../../utils/date";
import { OrderBookTable } from "./OrderBookTable";

export default function PredictiveDetail() {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { slug } = useParams();
  const id = slug?.split("&id=")[1];
  const productId = id ? Number(id) : 0;
  const isLargerThan991 = useMediaQuery("(min-width: 991px)");

  const [selectedInstrument, setSelectedInstrument] =
    useState<IPredictiveProductInstrument | null>(null);
  const [selectedInstrumentsIds, setSelectedInstrumentsIds] = useState<
    number[]
  >([]);
  const [selectedSide, setSelectedSide] = useState<ETypeInstrument>(
    ETypeInstrument.BUY
  );
  const [activeTab, setActiveTab] = useState<string | null>(null);

  const { data, isLoading } = usePredictiveProduct({
    id: productId,
    whitelabelId: whitelabel_id,
  });

  const navlinks = [
    { title: t("marketplace.title"), href: "/marketplace/predix" },
    {
      title: t("global.routes_names.predictives"),
      href: "/marketplace/predix",
    },
    { title: data?.title || "", href: "#" },
  ];

  const handleSelectInstrument = (
    instrument: IPredictiveProductInstrument,
    side: ETypeInstrument | null
  ) => {
    setSelectedInstrument(instrument);
    if (side) {
      setSelectedSide(side);
    }
    // Se clicar em Sim/Não, sempre garante que o book está expandido
    if (!selectedInstrumentsIds.includes(instrument.id)) {
      setSelectedInstrumentsIds([...selectedInstrumentsIds, instrument.id]);
    }
  };

  const handleSelectInstrumentsIds = (id: number) => {
    if (selectedInstrumentsIds.includes(id)) {
      setSelectedInstrumentsIds(
        selectedInstrumentsIds.filter((item) => item !== id)
      );
    } else {
      setSelectedInstrumentsIds([...selectedInstrumentsIds, id]);
    }
  };

  const handleTabChange = (value: string | null) => {
    // Se clicar na mesma tab, fecha (accordion)
    if (activeTab === value) {
      setActiveTab(null);
    } else {
      setActiveTab(value);
    }
  };

  const getDirectionColor = (direction: EInstrumentType) => {
    switch (direction) {
      case EInstrumentType.increase:
        return theme.colors.complementary[0];
      case EInstrumentType.decrease:
        return theme.colors.complementary[2];
      case EInstrumentType.neutral:
        return theme.colors.complementary[7];
      default:
        return theme.colors.basic[5];
    }
  };

  const sortedInstruments = data?.instruments?.slice().sort((a, b) => {
    const order = { decrease: 0, neutral: 1, increase: 2 };
    return (order[a.direction] ?? 3) - (order[b.direction] ?? 3);
  });

  const isNotFound = !isLoading && !data;

  // Dados de exemplo para o book de ofertas - você pode substituir pelos dados reais
  const mockBuyOrders = [
    { price: 92.3, contracts: 23 },
    { price: 75.3, contracts: 15 },
    { price: 91.1, contracts: 16 },
    { price: 82.8, contracts: 9 },
  ];

  const mockSellOrders = [
    { price: 7.7, contracts: 1 },
    { price: 24.5, contracts: 5 },
  ];

  if (isNotFound) {
    return (
      <Center
        px={{ base: 24, xl: 20 }}
        mb="32px"
        pos="relative"
        sx={{
          ":before": {
            position: "absolute",
            zIndex: -1,
            top: "0",
            right: "0",
            background: theme.colors.other[3],
            content: "''",
            height: "300px",
            width: "100vw",
          },
        }}
      >
        <Flex
          pt="32px"
          direction="column"
          maw={1680}
          w="100%"
          gap={{ base: "24px", 991: "32px" }}
        >
          <Breadcrumb
            navlinks={[
              { title: t("marketplace.title"), href: "/marketplace" },
              {
                title: t("global.routes_names.predictives"),
                href: "/marketplace/predix",
              },
            ]}
          />

          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="24px"
            p="48px"
            bg={theme.colors.basic[0]}
            style={{
              boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
              borderRadius: "12px",
              minHeight: "400px",
            }}
          >
            <Text fz="24px" fw={600} color="basic.5" ta="center">
              {t("marketplace.predix.detalhes.erro.title")}
            </Text>
            <Text fz="14px" color="basic.4" ta="center" maw={400}>
              {t("marketplace.predix.detalhes.erro.description")}
            </Text>
            <Button
              variant="primary"
              onClick={() => navigate("/marketplace/predix")}
              styles={{
                root: {
                  minHeight: "44px",
                  borderRadius: "10px",
                  padding: "0 32px",
                },
              }}
            >
              {t("marketplace.predix.detalhes.erro.button")}
            </Button>
          </Flex>
        </Flex>
      </Center>
    );
  }

  return (
    <Flex
      direction="column"
      w="100%"
      align={"center"}
      bg={theme.colors.other[0]}
      mih="100%"
    >
      <Flex
        w="100%"
        id={"predix"}
        justify={"center"}
        pb={{ base: "1rem", 991: "2rem" }}
        bg={theme.colors.other[3]}
        px={{ base: "1.5rem", 991: "3.75rem" }}
      >
        <Flex
          align={"start"}
          justify={"center"}
          mb={"unset"}
          maw="105rem"
          w="100%"
          gap="1rem"
          direction={"column"}
          style={{ position: "relative" }}
        >
          <Breadcrumb navlinks={navlinks} />
          <InfoPredix />
          <Text
            display={{ base: "none", 991: "block" }}
            fw={700}
            fz={{ base: "20vw", 991: "15vw", 1920: "17.5rem" }}
            lh={"100%"}
            sx={{
              position: "absolute",
              right: "0",
              top: "0",
              whiteSpace: "nowrap",
              background: `linear-gradient(180deg, ${theme.colors.basic[0]} -50%, rgba(255, 255, 255, 0) 85%)`,
              backgroundClip: "text",
              textFillColor: "transparent",
            }}
          >
            {"PrediX"}
          </Text>
        </Flex>
      </Flex>
      <Flex
        h="fit-content"
        direction="column"
        w="100%"
        justify={"center"}
        align={"center"}
        bg={`linear-gradient(180deg,${theme.colors.other[3]} 2rem, ${theme.colors.other[0]} 2rem)`}
        px={{ base: "1.5rem", 991: "3.75rem" }}
      >
        <Flex id="predix-details" mb="2rem" mih="100%" maw="105rem" w="100%">
          <Flex direction="column" w="100%" gap={{ base: "1.5rem", 991: "2rem" }}>

        <Flex gap="24px" w="100%" wrap={{ base: "wrap", 1200: "nowrap" }}>
          <Flex
            direction="column"
            gap="24px"
            w="100%"
            p={{ base: "16px", 1500: "24px" }}
            bg={theme.colors.basic[0]}
            style={{
              boxShadow: "0px 25px 35px rgba(0, 0, 0, 0.03)",
              borderRadius: "12px",
              flex: 1,
            }}
          >
            <Flex
              gap="16px"
              align="center"
              justify="space-between"
              wrap={{ base: "wrap", 700: "nowrap" }}
            >
              <Flex gap="16px" align="center">
                {isLoading ? (
                  <Skeleton height={56} width={56} radius={8} />
                ) : (
                  <Avatar
                    variant="outline"
                    size={56}
                    color="basic.2"
                    radius={8}
                    src={data?.icon}
                    alt={data?.title || "--"}
                    styles={{
                      root: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        border: `1.5px solid ${theme.colors.basic[2]}`,
                      },
                      image: {
                        height: "56px",
                        width: "56px",
                        objectFit: "contain",
                      },
                    }}
                  >
                    <Profile color={theme.colors.basic[5]} />
                  </Avatar>
                )}
                <Flex direction="column" gap="4px">
                  {isLoading ? (
                    <>
                      <Skeleton height={24} width={300} />
                      <Skeleton height={16} width={150} />
                    </>
                  ) : (
                    <>
                      <Text
                        fz={{ base: "16px", 700: "20px" }}
                        fw={600}
                        color="basic.5"
                        lh="130%"
                      >
                        {data?.title}
                      </Text>
                    </>
                  )}
                </Flex>
              </Flex>

              <PredictiveDateSelector
                currentData={data ? {
                  id: data.id,
                  title: data.title,
                  expiration: data.expiration,
                  category: data.category,
                } : undefined}
                isLoading={isLoading}
              />
            </Flex>

            {/* <Flex direction="column" gap="16px">
              <Tabs
                value={activeTab}
                onTabChange={handleTabChange}
                variant="default"
                styles={{
                  tabLabel: {
                    fontWeight: 600,
                  },
                  tab: {
                    "&[data-active]": {
                      color: theme.colors.primary[1],
                      ":hover": {
                        color: theme.colors.primary[1],
                        borderBottomColor: theme.colors.primary[1],
                        filter: "brightness(1.2)",
                      },
                    },
                    fontWeight: 600,
                    ":hover": {
                      color: theme.colors.primary[1],
                      borderBottomColor: theme.colors.primary[1],
                    },
                  },
                }}
              >
                <Tabs.List>
                  <Tabs.Tab value="probabilidades">
                    Gráfico de Probabilidades
                  </Tabs.Tab>
                  <Tabs.Tab value="grafico_di">Gráfico DI</Tabs.Tab>
                </Tabs.List>
              </Tabs>

              <Collapse in={activeTab === "probabilidades"}>
                <Flex p="16px" style={{ borderRadius: "8px" }}>
                  <Text color="basic.5">
                    Conteúdo do Gráfico de Probabilidades
                  </Text>
                </Flex>
              </Collapse>

              <Collapse in={activeTab === "grafico_di"}>
                <Flex p="16px" style={{ borderRadius: "8px" }}>
                  <Text color="basic.5">Conteúdo do Gráfico DI</Text>
                </Flex>
              </Collapse>
            </Flex> */}

            <Table
              horizontalSpacing="md"
              verticalSpacing="sm"
              striped={false}
              highlightOnHover
              withBorder={false}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      color: theme.colors.basic[4],
                      fontWeight: 500,
                      fontSize: "14px",
                      borderBottom: "none",
                    }}
                  >
                    {t("marketplace.predix.detalhes.table.option")}
                    Opção
                  </th>
                  <th
                    style={{
                      color: theme.colors.basic[4],
                      fontWeight: 500,
                      fontSize: "14px",
                      borderBottom: "none",
                      textAlign: "center",
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? Array.from({ length: 5 }).map((_, index) => (
                      <tr key={index}>
                        <td>
                          <Skeleton height={24} width={120} />
                        </td>
                        <td>
                          <Skeleton height={32} width={200} />
                        </td>
                      </tr>
                    ))
                  : sortedInstruments?.map((instrument) => {
                      const isSelected =
                        selectedInstrument?.id === instrument.id;
                      const isSelectedInstrument =
                        selectedInstrumentsIds.includes(instrument.id);
                      return (
                        <>
                          <tr
                            key={instrument.id}
                            style={{
                              borderTop: isSelected
                                ? "1px solid #E0E0E0"
                                : "1px solid #d7d7d7",
                              borderBottom: isSelected
                                ? "1px solid #E0E0E0"
                                : "1px solid #d7d7d7",
                              // borderBottom: isSelectedInstrument
                              //   ? "none"
                              //   : "1px solid #d7d7d7",
                              // cursor: "pointer",
                            }}
                            // onClick={() =>
                            //   handleSelectInstrumentsIds(instrument.id)
                            // }
                          >
                            <td
                              style={{
                                border: "none",
                                padding: "12px 16px",
                              }}
                            >
                              <Text
                                fz="14px"
                                fw={600}
                                color={getDirectionColor(instrument.direction)}
                              >
                                {instrument.label}
                              </Text>
                            </td>
                            <td
                              style={{
                                border: "none",
                                padding: "12px 16px",
                              }}
                            >
                              <Flex
                                gap="16px"
                                justify="flex-end"
                                align="center"
                              >
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectInstrument(
                                      instrument,
                                      ETypeInstrument.BUY
                                    );
                                  }}
                                  variant="optionYes"
                                  data-active={
                                    isSelected &&
                                    selectedSide === ETypeInstrument.BUY
                                  }
                                  styles={{
                                    root: {
                                      minWidth: "160px",
                                      minHeight: "44px !important",
                                      borderRadius: "10px !important",
                                      padding: "0 24px",
                                    },
                                    label: {
                                      fontSize: "16px",
                                    },
                                  }}
                                >
                                  {`${t("global.predictives.yes")}   ${
                                    instrument.level1_formatted?.ask_price ||
                                    "R$ 0"
                                  }`}
                                </Button>
                                <Button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectInstrument(
                                      instrument,
                                      ETypeInstrument.SELL
                                    );
                                  }}
                                  variant="optionNo"
                                  data-active={
                                    isSelected &&
                                    selectedSide === ETypeInstrument.SELL
                                  }
                                  styles={{
                                    root: {
                                      minWidth: "160px",
                                      minHeight: "44px !important",
                                      borderRadius: "10px !important",
                                      padding: "0 24px",
                                    },
                                    label: {
                                      fontSize: "16px",
                                    },
                                  }}
                                >
                                  {`${t("global.predictives.no")}   ${
                                    instrument.calculated_ask_formatted ||
                                    "R$ 0"
                                  }`}
                                </Button>
                              </Flex>
                            </td>
                          </tr>
                          {/* book de Ofertas */}
                          {/* <tr
                            key={`${instrument.id}-collapse`}
                            style={{
                              display: isSelectedInstrument
                                ? "table-row"
                                : "none",
                              borderTop: isSelectedInstrument
                                ? "none"
                                : "1px solid #d7d7d7",
                              borderBottom: isSelectedInstrument
                                ? "1px solid #E0E0E0"
                                : "1px solid #d7d7d7",
                              backgroundColor: "transparent",
                            }}
                          >
                            <td
                              colSpan={2}
                              style={{ border: "none", padding: "0px 16px" }}
                            >
                              <Collapse in={isSelectedInstrument}>
                                <Flex direction="column">
                                  <Flex
                                    gap="15px"
                                    p="12px 0"
                                    w="fit-content"
                                    style={{
                                      borderBottom: "2px solid",
                                      borderBottomColor:
                                        theme.colors.primary[1],
                                    }}
                                  >
                                    <Text
                                      fz="14px"
                                      fw={700}
                                      color="primary.1"
                                      lh="10px"
                                    >
                                      {t(
                                        "marketplace.predix.detalhes.table.book"
                                      )}
                                    </Text>
                                  </Flex>
                                  <Flex gap="16px" wrap="wrap" py="16px">
                                    <OrderBookTable
                                      type="buy"
                                      orders={mockBuyOrders}
                                    />
                                    <OrderBookTable
                                      type="sell"
                                      orders={mockSellOrders}
                                    />
                                  </Flex>
                                </Flex>
                              </Collapse>
                            </td>
                          </tr> */}
                        </>
                      );
                    })}
              </tbody>
            </Table>
          </Flex>

          {isLargerThan991 && (
            <PredictiveBuyCard
              data={data}
              selectedInstrument={selectedInstrument}
              selectedSide={selectedSide}
              onSideChange={setSelectedSide}
            />
          )}
        </Flex>

        {!isLargerThan991 && (
          <PredictiveBuyCard
            data={data}
            selectedInstrument={selectedInstrument}
            selectedSide={selectedSide}
            onSideChange={setSelectedSide}
          />
        )}
      </Flex>
      </Flex>
      </Flex>
    </Flex>
  );
}
